<script setup>
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { sendBradleyFeedback } from '../services/bradley-ai.service.js'

const props = defineProps({
  open: {
    type: Boolean,
    required: true,
  },
  conversationId: {
    type: [String, Number],
    default: null,
  },
  message: {
    type: Object,
    default: null,
  },
  messageIndex: {
    type: Number,
    default: null,
  },
})

const emit = defineEmits(['close', 'success'])

const { t } = useI18n()

const feedbackOptions = ref([
  { name: t('newBradley.dislikeModal.personality'), key: 'personality' },
  { name: t('newBradley.dislikeModal.incorrectResponse'), key: 'incorrectResponse' },
  { name: t('newBradley.dislikeModal.writingFormatting'), key: 'writingFormatting' },
  { name: t('newBradley.dislikeModal.lostContext'), key: 'lostContext' },
  { name: t('newBradley.dislikeModal.other'), key: 'other' },
])
const selectedCategories = ref([])
const details = ref('')
const isSubmitting = ref(false)

function handleClose() {
  selectedCategories.value = []
  details.value = ''
  emit('close')
}

function categoryKeyToApiLabel(key) {
  const map = {
    personality: 'personality',
    incorrectResponse: 'incorrect response',
    writingFormatting: 'writing formatting',
    lostContext: 'lost context',
    other: 'other',
  }
  return map[key] ?? key
}

async function handleSubmit() {
  if (!props.conversationId || !props.message?.message_id || isSubmitting.value) {
    return
  }

  const categories = selectedCategories.value.map(categoryKeyToApiLabel)

  try {
    isSubmitting.value = true
    await sendBradleyFeedback(props.conversationId, {
      message_id: props.message.message_id,
      user_like: false,
      categories,
    })

    selectedCategories.value = []
    details.value = ''
    emit('success')
  }
  catch (error) {
    console.error('Failed to send dislike feedback:', error)
  }
  finally {
    isSubmitting.value = false
  }
}
</script>

<template>
  <teleport to="body">
    <transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="transform opacity-0"
      enter-to-class="transform opacity-100"
      leave-active-class="transition ease-in duration-150"
      leave-from-class="transform opacity-100"
      leave-to-class="transform opacity-0"
    >
      <div
        v-if="open"
        class="fixed inset-0 z-[100] flex items-center justify-center p-4"
        @click.self="handleClose"
      >
        <div class="inset-0 z-[-1] bg-black opacity-40 w-full h-full absolute top-0 left-0" />
        <div class="flex w-full max-w-[590px] flex-col overflow-hidden rounded-2xl bg-white">
          <div class="flex items-center justify-between px-6 py-4">
            <h2 class="font-inter text-xl font-bold leading-none tracking-[0.005em]">
              {{ $t('newBradley.dislikeModal.title') }}
            </h2>
            <button
              class="rounded-lg p-2 transition-colors hover:bg-gray-100"
              @click="handleClose"
            >
              <i class="pi pi-times text-lg text-gray-600" />
            </button>
          </div>

          <div class="px-6 py-3">
            <p class="text-sm text-gray-600">
              {{ $t('newBradley.dislikeModal.subtitle') }}
            </p>
          </div>

          <div class="flex-1 overflow-y-auto px-6 py-4">
            <p class="text-sm text-gray-700 font-medium mb-3">
              {{ $t('newBradley.dislikeModal.selectAllThatApply') }}
            </p>

            <div
              v-for="option of feedbackOptions"
              :key="option.key"
              class="flex items-center gap-3 h-10"
            >
              <input
                :id="option.key"
                v-model="selectedCategories"
                type="checkbox"
                :value="option.name"
                class="w-4 h-4 text-blue-600 bg-gray-100 rounded cursor-pointer border-dss-surface-300"
              >
              <label
                :for="option.key"
                class="cursor-pointer text-sm text-gray-700"
              >{{ option.name }}</label>
            </div>

            <div class="mt-6">
              <label class="text-sm text-gray-700 font-medium mb-2 block">
                {{ $t('newBradley.dislikeModal.additionalDetails') }} ({{ $t('newBradley.dislikeModal.optional') }})
              </label>
              <textarea
                v-model="details"
                rows="4"
                class="w-full p-3 border border-dss-surface-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                :placeholder="$t('newBradley.dislikeModal.detailsPlaceholder')"
              />
            </div>
          </div>

          <div class="px-6 py-4 border-t border-dss-surface-200 flex justify-end gap-3">
            <button
              class="py-2 px-3 border border-[#B0B2DA] text-sm hover:bg-gray-200 text-gray-700 font-semibold rounded-lg transition-colors cursor-pointer"
              @click="handleClose"
            >
              {{ $t('newBradley.dislikeModal.cancel') }}
            </button>
            <button
              class="py-2 px-3 bg-dss-primary-500 hover:bg-dss-primary-600 font-hellix font-medium text-sm leading-none text-white rounded-lg transition-colors cursor-pointer"
              :disabled="isSubmitting || selectedCategories.length === 0"
              @click="handleSubmit"
            >
              <i
                v-if="isSubmitting"
                class="pi pi-spinner animate-spin"
              />
              {{ isSubmitting ? $t('newBradley.dislikeModal.submitting') : $t('newBradley.dislikeModal.submit') }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>
